/**
 * /eleve/mes-shorts — Back-office élève : "Mes Shorts"
 *
 * L'élève/membre y retrouve toutes les vidéos qu'il/elle a proposées, classées
 * par statut : en attente de modération, publiées, refusées (avec motif).
 *
 * - Fetch : api.shortsMine()
 * - CTA : "Proposer un nouveau Short" → /shorts/proposer
 * - États : loading / empty / data (grouped by status).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Clapperboard, Clock, CheckCircle2, XCircle, Eye, Film,
  AlertTriangle, Plus, ExternalLink, Youtube, Pencil, Trash2, Filter, X, Check,
} from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';

const THEME_LABELS = {
  equipement: 'Équipement',
  etm: 'ETM',
  plateau: 'Plateau',
  circulation: 'Circulation',
  trajectoires: 'Trajectoires',
  freinage: 'Freinage',
  'angles-morts': 'Angles morts',
  interfiles: 'Interfiles',
  meteo: 'Météo',
  mentale: 'Préparation mentale',
  'gestes-signaleur': 'Gestes du signaleur',
  autre: 'Autre',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch (_) { return '—'; }
};

const Thumb = ({ s }) => {
  // Preview : YouTube → poster hqdefault, upload/static → poster ou fallback générique
  if (s.youtube_id) {
    return (
      <img
        src={`https://i.ytimg.com/vi/${s.youtube_id}/hqdefault.jpg`}
        alt=""
        loading="lazy"
        className="w-full h-full object-cover"
      />
    );
  }
  if (s.poster_url) {
    return <img src={s.poster_url} alt="" loading="lazy" className="w-full h-full object-cover" />;
  }
  return (
    <div className="w-full h-full bg-gradient-to-br from-rose-900/40 to-red-950/60 flex items-center justify-center">
      <Film className="w-8 h-8 text-white/50" />
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: { label: 'En attente', icon: Clock, cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
    published: { label: 'Publié', icon: CheckCircle2, cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
    rejected: { label: 'Refusé', icon: XCircle, cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${cfg.cls}`}>
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

const ShortCard = ({ s, highlight, onEdit, onDelete }) => {
  const theme = THEME_LABELS[s.theme] || 'Autre';
  const external = s.source_url_original || s.embed_url || null;
  const isPending = s.status === 'pending';
  return (
    <article
      className={`relative rounded-xl overflow-hidden border bg-[#0d0d0e] transition hover:border-white/25 ${
        highlight ? 'border-amber-500/40' : 'border-white/10'
      }`}
      data-testid={`eleve-short-${s.id}`}
    >
      <div className="aspect-[9/16] sm:aspect-video bg-black relative">
        <Thumb s={s} />
        <div className="absolute top-2 left-2">
          <StatusBadge status={s.status} />
        </div>
        {s.status === 'published' && (
          <div className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-bold">
            <Eye className="w-3 h-3" /> {s.views ?? 0}
          </div>
        )}
      </div>
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.14em] text-rose-400 font-black">
          <span>{theme}</span>
          <span className="text-white/30">·</span>
          <span className="text-white/50">{formatDate(s.created_at)}</span>
        </div>
        <h3 className="text-[14px] font-black text-white leading-snug line-clamp-2" title={s.title}>
          {s.title}
        </h3>
        {s.description && (
          <p className="text-[12px] text-white/60 line-clamp-2">{s.description}</p>
        )}

        {/* ======== Feedback pédagogique du moniteur (si présent) ======== */}
        {s.teacher_comment && (
          <div className="mt-2 rounded-lg border border-indigo-500/30 bg-indigo-950/30 p-2 text-[11.5px] text-indigo-200 leading-snug">
            <div className="font-bold mb-0.5 inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Retour moniteur
              {s.teacher_feedback_by && <span className="text-indigo-300/60 font-normal">· {s.teacher_feedback_by}</span>}
            </div>
            <div className="text-indigo-200/80">{s.teacher_comment}</div>
          </div>
        )}

        {s.status === 'rejected' && s.reject_reason && (
          <div className="mt-2 rounded-lg border border-red-500/30 bg-red-950/30 p-2 text-[11.5px] text-red-200 leading-snug">
            <div className="font-bold mb-0.5 inline-flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Motif du refus
            </div>
            <div className="text-red-200/80">{s.reject_reason}</div>
          </div>
        )}

        <div className="flex items-center gap-1.5 pt-1.5 flex-wrap">
          {s.status === 'published' && (
            <Link
              to="/shorts"
              data-testid={`eleve-short-view-${s.id}`}
              className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-1 h-8 px-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-[11px] font-black uppercase tracking-wider transition"
            >
              <Film className="w-3 h-3" /> Voir en live
            </Link>
          )}
          {isPending && onEdit && (
            <button
              type="button"
              onClick={() => onEdit(s)}
              data-testid={`eleve-short-edit-${s.id}`}
              className="inline-flex items-center justify-center gap-1 h-8 px-2 rounded-lg bg-amber-600/90 hover:bg-amber-500 text-white text-[11px] font-black uppercase tracking-wider transition"
              aria-label="Modifier"
            >
              <Pencil className="w-3 h-3" /> Modifier
            </button>
          )}
          {isPending && onDelete && (
            <button
              type="button"
              onClick={() => onDelete(s)}
              data-testid={`eleve-short-delete-${s.id}`}
              className="inline-flex items-center justify-center gap-1 h-8 px-2 rounded-lg bg-red-700/80 hover:bg-red-600 text-white text-[11px] font-black uppercase tracking-wider transition"
              aria-label="Supprimer"
            >
              <Trash2 className="w-3 h-3" /> Supprimer
            </button>
          )}
          {external && (
            <a
              href={external}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1 h-8 px-2 rounded-lg border border-white/15 text-white/70 hover:text-white hover:border-white/35 text-[11px] font-semibold transition"
              aria-label="Lien source"
            >
              {s.source_type === 'youtube' ? <Youtube className="w-3 h-3" /> : <ExternalLink className="w-3 h-3" />}
              Source
            </a>
          )}
        </div>
      </div>
    </article>
  );
};

const Section = ({ title, items, color, icon: Icon, emptyLabel, highlight, onEdit, onDelete }) => {
  return (
    <section className="space-y-3">
      <header className="flex items-center gap-2">
        <span
          className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-white shadow"
          style={{ background: color }}
        >
          <Icon className="w-3.5 h-3.5" />
        </span>
        <h2 className="text-[13px] font-black uppercase tracking-[0.18em] text-white">
          {title} <span className="text-white/40 ml-1">({items.length})</span>
        </h2>
      </header>
      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.02] p-6 text-center">
          <p className="text-white/50 text-[12.5px]">{emptyLabel}</p>
        </div>
      ) : (
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
          {items.map((s) => (
            <ShortCard key={s.id} s={s} highlight={highlight} onEdit={onEdit} onDelete={onDelete} />
          ))}
        </div>
      )}
    </section>
  );
};

export default function MesShortsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [shorts, setShorts] = useState([]);
  const [themeFilter, setThemeFilter] = useState('all');
  // Modal édition
  const [editOpen, setEditOpen] = useState(false);
  const [editShort, setEditShort] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  // Modal suppression
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteShort, setDeleteShort] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    document.title = 'Mes Shorts · Espace élève';
    (async () => {
      try {
        const data = await api.shortsMine();
        setShorts(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e?.status === 401) {
          navigate('/login');
          return;
        }
        toast.error('Impossible de charger vos Shorts.');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  // Scroll automatique vers la section demandée via ?status=pending|published|rejected
  // (déclenché par les 3 pastilles cliquables de /eleve/dashboard "Ma Journée").
  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(location.search || '');
    const target = params.get('status');
    if (!target) return;
    // Petit délai pour laisser le DOM se peindre après le fetch
    const t = setTimeout(() => {
      const el = document.getElementById(`eleve-shorts-section-${target}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Highlight visuel bref (ring rose) pour confirmer que la section est bien ciblée
        el.classList.add('ring-2', 'ring-rose-500/60', 'rounded-2xl', 'transition-all');
        setTimeout(() => {
          el.classList.remove('ring-2', 'ring-rose-500/60');
        }, 2200);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [loading, location.search]);

  // === Édition ===
  const openEdit = (s) => {
    setEditShort(s);
    setEditTitle(s.title || '');
    setEditDesc(s.description || '');
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!editShort) return;
    const t = editTitle.trim();
    if (t.length < 4) { toast.error('Titre trop court (4 car. min)'); return; }
    setEditSaving(true);
    try {
      await api.shortsMineEdit(editShort.id, { title: t, description: editDesc.trim() });
      setShorts((prev) => prev.map((x) => (x.id === editShort.id ? { ...x, title: t, description: editDesc.trim() } : x)));
      toast.success('Short mis à jour');
      setEditOpen(false);
    } catch (e) {
      toast.error(e?.message || 'Impossible de mettre à jour');
    } finally {
      setEditSaving(false);
    }
  };

  // === Suppression ===
  const openDelete = (s) => { setDeleteShort(s); setDeleteOpen(true); };
  const confirmDelete = async () => {
    if (!deleteShort) return;
    setDeleteBusy(true);
    try {
      await api.shortsMineDelete(deleteShort.id);
      setShorts((prev) => prev.filter((x) => x.id !== deleteShort.id));
      toast.success('Short supprimé');
      setDeleteOpen(false);
    } catch (e) {
      toast.error(e?.message || 'Suppression impossible');
    } finally {
      setDeleteBusy(false);
    }
  };

  // Filtre thème appliqué AVANT regroupement par statut
  const filteredShorts = useMemo(() => {
    if (themeFilter === 'all') return shorts;
    return shorts.filter((s) => s.theme === themeFilter);
  }, [shorts, themeFilter]);

  const { pending, published, rejected } = useMemo(() => ({
    pending: filteredShorts.filter((s) => s.status === 'pending'),
    published: filteredShorts.filter((s) => s.status === 'published'),
    rejected: filteredShorts.filter((s) => s.status === 'rejected'),
  }), [filteredShorts]);

  // Liste des thèmes présents dans les shorts de l'élève (pour alimenter le filtre)
  const availableThemes = useMemo(() => {
    const set = new Set(shorts.map((s) => s.theme).filter(Boolean));
    return Array.from(set);
  }, [shorts]);

  const totalViews = published.reduce((sum, s) => sum + (s.views || 0), 0);

  return (
    <div className="max-w-6xl space-y-8" data-testid="eleve-mes-shorts">
      {/* ============ HEADER ============ */}
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-rose-400 font-black mb-1 inline-flex items-center gap-1.5">
            <Clapperboard className="w-3 h-3" /> Mes contributions
          </div>
          <h1
            className="text-white text-[28px] sm:text-[36px] font-black leading-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Mes Shorts
          </h1>
          <p className="text-white/55 text-[13.5px] mt-1 max-w-xl leading-relaxed">
            Retrouvez ici toutes les vidéos que vous avez proposées à Code 2 Roues
            (en attente, publiées ou refusées).
          </p>
        </div>

        <Link
          to="/shorts/proposer"
          data-testid="eleve-mes-shorts-cta-propose"
          className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-black uppercase tracking-wider text-[12px] shadow-lg shadow-rose-900/40 transition"
        >
          <Plus className="w-4 h-4" /> Proposer un Short
        </Link>
      </header>

      {/* ============ STATS RAPIDES ============ */}
      <section className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4" data-testid="stat-pending">
          <div className="text-[10px] uppercase tracking-[0.16em] text-amber-300 font-bold">En attente</div>
          <div className="text-white text-3xl font-black mt-1">{pending.length}</div>
        </div>
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4" data-testid="stat-published">
          <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-300 font-bold">Publiés</div>
          <div className="text-white text-3xl font-black mt-1">{published.length}</div>
          {totalViews > 0 && (
            <div className="text-emerald-200/70 text-[11px] mt-1 inline-flex items-center gap-1">
              <Eye className="w-3 h-3" /> {totalViews.toLocaleString('fr-FR')} vues cumulées
            </div>
          )}
        </div>
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4" data-testid="stat-rejected">
          <div className="text-[10px] uppercase tracking-[0.16em] text-red-300 font-bold">Refusés</div>
          <div className="text-white text-3xl font-black mt-1">{rejected.length}</div>
        </div>
      </section>

      {/* ============ FILTRE THÈME (visible seulement si l'élève a des shorts) ============ */}
      {availableThemes.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap -mb-4" data-testid="eleve-shorts-filter">
          <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.14em] text-white/50">
            <Filter className="w-3 h-3" /> Thème :
          </span>
          <button
            type="button"
            onClick={() => setThemeFilter('all')}
            className={`px-2.5 h-7 rounded-full text-[11px] font-bold uppercase tracking-wider transition ${themeFilter === 'all' ? 'bg-rose-600 text-white' : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10'}`}
            data-testid="eleve-shorts-filter-all"
          >
            Tous
          </button>
          {availableThemes.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setThemeFilter(t)}
              className={`px-2.5 h-7 rounded-full text-[11px] font-bold uppercase tracking-wider transition ${themeFilter === t ? 'bg-rose-600 text-white' : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10'}`}
              data-testid={`eleve-shorts-filter-${t}`}
            >
              {THEME_LABELS[t] || t}
            </button>
          ))}
        </div>
      )}

      {/* ============ CONTENU ============ */}
      {loading ? (
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-10 text-center text-white/50">
          Chargement de vos Shorts…
        </div>
      ) : shorts.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-rose-950/30 to-black p-10 text-center space-y-4">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-rose-600/20 text-rose-400 mx-auto">
            <Clapperboard className="w-7 h-7" />
          </div>
          <div>
            <h2 className="text-white text-[20px] font-black">Aucun Short proposé pour l'instant</h2>
            <p className="text-white/55 text-[13px] mt-1 max-w-lg mx-auto">
              Partagez un conseil, un geste technique ou votre expérience en moins de 60 secondes —
              l'équipe modère puis publie votre vidéo sur la rubrique Shorts du site.
            </p>
          </div>
          <Link
            to="/shorts/proposer"
            data-testid="eleve-mes-shorts-empty-cta"
            className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-black uppercase tracking-wider text-[12px] shadow-lg shadow-rose-900/40 transition"
          >
            <Plus className="w-4 h-4" /> Proposer mon premier Short
          </Link>
        </div>
      ) : (
        <div className="space-y-10">
          <div id="eleve-shorts-section-pending">
            <Section
              title="En attente de modération"
              items={pending}
              color="#d97706"
              icon={Clock}
              highlight
              onEdit={openEdit}
              onDelete={openDelete}
              emptyLabel="Aucun Short en attente — tout est à jour ✨"
            />
          </div>
          <div id="eleve-shorts-section-published">
            <Section
              title="Publiés sur le site"
              items={published}
              color="#059669"
              icon={CheckCircle2}
              emptyLabel="Vos Shorts validés apparaîtront ici."
            />
          </div>
          <div id="eleve-shorts-section-rejected">
            <Section
              title="Non retenus"
              items={rejected}
              color="#dc2626"
              icon={XCircle}
              emptyLabel="Aucun Short refusé — joli parcours."
            />
          </div>
        </div>
      )}

      {/* ============ MODAL ÉDITION ============ */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="eleve-shorts-edit-dialog">
          <DialogHeader>
            <DialogTitle>Modifier ton Short</DialogTitle>
            <DialogDescription>
              Tu peux modifier le titre et la description tant que la modération n'a pas statué.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-[11px] font-black uppercase tracking-wider text-white/60 block mb-1">Titre</label>
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Titre du Short"
                maxLength={90}
                data-testid="eleve-shorts-edit-title"
              />
              <div className="text-[10px] text-white/40 mt-1">{editTitle.length}/90</div>
            </div>
            <div>
              <label className="text-[11px] font-black uppercase tracking-wider text-white/60 block mb-1">Description (optionnelle)</label>
              <Textarea
                value={editDesc}
                onChange={(e) => setEditDesc(e.target.value)}
                placeholder="Un contexte, un conseil…"
                maxLength={300}
                rows={3}
                data-testid="eleve-shorts-edit-desc"
              />
              <div className="text-[10px] text-white/40 mt-1">{editDesc.length}/300</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)} disabled={editSaving}>Annuler</Button>
            <Button onClick={saveEdit} disabled={editSaving} data-testid="eleve-shorts-edit-save">
              <Check className="w-4 h-4 mr-1.5" />
              {editSaving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ MODAL SUPPRESSION ============ */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent data-testid="eleve-shorts-delete-dialog">
          <DialogHeader>
            <DialogTitle>Supprimer ce Short ?</DialogTitle>
            <DialogDescription>
              Cette action est définitive. Le short sera retiré de la file de modération.
              Les shorts déjà publiés ou refusés ne peuvent pas être supprimés.
            </DialogDescription>
          </DialogHeader>
          {deleteShort && (
            <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-[13px] text-white/80">
              « {deleteShort.title} »
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteOpen(false)} disabled={deleteBusy}>Annuler</Button>
            <Button
              onClick={confirmDelete}
              disabled={deleteBusy}
              className="bg-red-600 hover:bg-red-500"
              data-testid="eleve-shorts-delete-confirm"
            >
              <Trash2 className="w-4 h-4 mr-1.5" />
              {deleteBusy ? 'Suppression…' : 'Supprimer définitivement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ RAPPEL CONDITIONS ============ */}
      <footer className="rounded-xl border border-white/10 bg-white/[0.02] p-4 text-[12px] text-white/55 leading-relaxed">
        En soumettant un Short, vous acceptez les{' '}
        <Link to="/videos/conditions" className="text-rose-300 hover:text-rose-200 underline underline-offset-2">
          conditions de contribution
        </Link>{' '}
        (contenu respectueux, droits, modération éditoriale). Vous recevrez un email à chaque décision.
      </footer>
    </div>
  );
}
